<template>
  <div
    :class="[
      $style.base,
      {
        [$style.hasCustomColor]: !!color,
        [$style.default]: !color,
        [$style.disabled]: state === 'disabled',
        [$style.selected]: state === 'selected',
        [$style.closeable]: !!closeable,
        [$style.clickable]: !!$attrs.onClick
      }
    ]"
  >
    <div :class="$style.inner">
      <div
        :class="$style.content"
        v-bind="$attrs.onClick ? ({ onClick: $attrs.onClick } as HTMLAttributes) : {}"
      >
        <transition>
          <div
            v-if="state === 'selected'"
            :class="$style.icon"
          >
            <BaseIcon
              name="check"
              color="inherit"
              size="s"
            />
          </div>
          <div
            v-else-if="icon"
            :class="$style.icon"
          >
            <BaseIcon
              :name="icon"
              size="s"
              :color="color ? 'fixed' : 'default'"
            />
          </div>
        </transition>
        <div :class="$style.text">{{ text }}</div>
      </div>
      <div
        v-if="closeable"
        :class="$style.close"
        @click="$emit('close')"
        v-test="'_base-chip-delete'"
      >
        <BaseIcon
          name="close"
          :color="state === 'selected' ? 'inherit' : (color ? 'fixed' : 'default')"
          size="s"
        />
      </div>
    </div>
    <div
      :class="$style.background"
      :style="color ? { 'background-color': backgroundColor, 'border-color': color } : {}"
    />
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script
  setup
  lang="ts"
>
import type { HTMLAttributes } from 'vue';
import type { IconName } from '@/components/base-icon/types';
import { lightColor } from '@/helpers/formatting';

const props = defineProps<{
  closeable?: boolean;
  color?: string;
  icon?: IconName;
  text: string;
  state?: 'disabled' | 'selected';
}>();

defineEmits(['close']);

const backgroundColor = computed(() =>
  props.color ? lightColor(props.color) : undefined,
);
</script>

<style
  lang="scss"
  module
>
.base {
  display: inline-block;
  position: relative;
  flex-shrink: 0;
  border-radius: $radius;
  overflow: hidden;
  max-width: 100%;

  &.disabled {
    pointer-events: none;
  }

  // The selected fill inverts to white in dark mode, so the check and close
  // icons follow --text-on-action-primary instead of staying white
  &.selected {
    color: var(--text-on-action-primary);
  }

  &.clickable {
    cursor: pointer;
    transition: transform 0.15s ease;

    @include hover {
      &.default:not(.selected) {
        .background {
          background-color: var(--highlight);
        }
      }
    }

    // Pressed: instant scale-down, eases back on release
    &:active {
      transform: scale(0.97);
      transition-duration: 0s;

      @media (prefers-reduced-motion: reduce) {
        transform: none;
      }

      &.default:not(.selected) .background {
        background-color: var(--highlight-strong);
      }
    }
  }
}

.text {
  font-size: 13px;
  letter-spacing: -0.01em;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  .base:not(.hasCustomColor):not(.selected) & {
    color: var(--text-primary);
  }

  .base.hasCustomColor:not(.selected) & {
    color: var(--text-fixed);
  }

  .base.selected & {
    color: var(--text-on-action-primary);
  }

  .base.disabled & {
    color: var(--text-placeholder);
  }
}

.inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 28px;
  max-width: 270px;
}

.content {
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 6px;
  overflow: hidden;

  .base.closeable & {
    padding-right: 0;
  }
}

.icon {
  display: flex;
  align-items: center;
  height: 100%;

  &:global(.v-enter-active) {
    transition:
      transform 0.2s $easeOutBack,
      opacity 0.1s;
  }

  &:global(.v-enter-from) {
    transform: scale(0.2);
    opacity: 0;
  }

  &:global(.v-leave-active) {
    position: absolute;
  }
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  height: 100%;
  cursor: pointer;
}

.background {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-width: 1px;
  border-style: solid;
  border-radius: inherit;
  transition: background-color 0.1s;

  .base.default & {
    border-color: var(--border-strong);
    background-color: var(--action-primary-softer);
  }

  .base.selected & {
    background-color: var(--action-primary) !important;
    border-color: var(--action-primary) !important;
  }

  .base.disabled & {
    background-color: var(--neutral-softer) !important;
    border-color: var(--border) !important;
  }
}
</style>
